import React, {useMemo, useRef, useState} from 'react';
import Typography from '@material-ui/core/Typography';
import InputBase from '@material-ui/core/InputBase';
import ButtonBase from '@material-ui/core/ButtonBase';
import Tooltip from '@material-ui/core/Tooltip';
import SearchRoundedIcon from '@material-ui/icons/SearchRounded';
import CloseRoundedIcon from '@material-ui/icons/CloseRounded';
import CheckRoundedIcon from '@material-ui/icons/CheckRounded';
import FilterNoneRoundedIcon from '@material-ui/icons/FilterNoneRounded';
import {createTheme, fade, makeStyles} from '@material-ui/core/styles';
import {FixedSizeList} from 'react-window';

const ROW_HEIGHT = 36;
const MAX_VISIBLE_ROWS = 5;
const VIRTUALIZE_FROM = 60;
const TOOLTIP_FROM = 26;

const scrollbarStyles = (theme) => ({
	'&::-webkit-scrollbar': {width: 6},
	'&::-webkit-scrollbar-track': {backgroundColor: 'transparent'},
	'&::-webkit-scrollbar-thumb': {
		backgroundColor: fade(theme.palette.text.primary, 0.18),
		borderRadius: 3,
		'&:hover': {
			backgroundColor: fade(theme.palette.text.primary, 0.32)
		}
	},
	scrollbarWidth: 'thin',
	scrollbarColor: `${fade(theme.palette.text.primary, 0.18)} transparent`
});

const useStyles = makeStyles((theme) => ({
	'@keyframes riseIn': {
		from: {opacity: 0, transform: 'translateY(10px) scale(0.985)'},
		to: {opacity: 1, transform: 'none'}
	},
	'@keyframes popIn': {
		'0%': {opacity: 0, transform: 'scale(0.4)'},
		'60%': {opacity: 1, transform: 'scale(1.12)'},
		'100%': {opacity: 1, transform: 'scale(1)'}
	},
	'@keyframes fadeIn': {
		from: {opacity: 0},
		to: {opacity: 1}
	},
	card: {
		display: 'flex',
		flexDirection: 'column',
		borderRadius: 12,
		border: `1px solid ${fade(theme.palette.text.primary, 0.12)}`,
		backgroundColor: theme.palette.type === 'dark'
			? fade(theme.palette.common.white, 0.03)
			: fade(theme.palette.common.black, 0.015),
		transition: theme.transitions.create(['border-color', 'box-shadow', 'transform'], {
			duration: 200,
			easing: 'cubic-bezier(0.4, 0, 0.2, 1)'
		}),
		animation: '$riseIn 340ms cubic-bezier(0.16, 1, 0.3, 1) both',
		animationDelay: ({index}) => `${Math.min(index, 9) * 45}ms`,
		'&:hover': {
			borderColor: fade(theme.palette.primary.main, 0.45),
			transform: 'translateY(-1px)',
			boxShadow: theme.palette.type === 'dark'
				? '0 10px 24px rgba(0, 0, 0, 0.35)'
				: '0 10px 24px rgba(15, 23, 42, 0.08)'
		},
		'&:focus-within': {
			borderColor: theme.palette.primary.main,
			boxShadow: `0 0 0 3px ${fade(theme.palette.primary.main, 0.22)}`
		}
	},
	cardActive: {
		borderColor: fade(theme.palette.primary.main, 0.55),
		backgroundColor: fade(theme.palette.primary.main, 0.06)
	},
	head: {
		display: 'flex',
		alignItems: 'center',
		padding: '10px 12px 8px 12px'
	},
	label: {
		flex: 1,
		minWidth: 0,
		fontSize: 11,
		fontWeight: 700,
		letterSpacing: '0.09em',
		textTransform: 'uppercase',
		color: theme.palette.text.secondary,
		overflow: 'hidden',
		textOverflow: 'ellipsis',
		whiteSpace: 'nowrap'
	},
	badge: {
		marginLeft: 8,
		minWidth: 20,
		height: 20,
		padding: '0 7px',
		borderRadius: 10,
		display: 'inline-flex',
		alignItems: 'center',
		justifyContent: 'center',
		fontSize: 11,
		fontWeight: 700,
		lineHeight: 1,
		color: theme.palette.getContrastText(theme.palette.primary.main),
		backgroundColor: theme.palette.primary.main,
		animation: '$popIn 260ms cubic-bezier(0.16, 1, 0.3, 1) both'
	},
	search: {
		display: 'flex',
		alignItems: 'center',
		margin: '0 12px',
		padding: '0 8px',
		height: 32,
		borderRadius: 8,
		border: `1px solid transparent`,
		backgroundColor: fade(theme.palette.text.primary, 0.06),
		transition: theme.transitions.create(['background-color', 'border-color'], {duration: 180}),
		'&:hover': {
			backgroundColor: fade(theme.palette.text.primary, 0.1)
		}
	},
	searchActive: {
		borderColor: fade(theme.palette.primary.main, 0.6),
		backgroundColor: fade(theme.palette.primary.main, 0.08)
	},
	searchIcon: {
		fontSize: 16,
		color: theme.palette.text.secondary,
		marginRight: 6
	},
	searchInput: {
		flex: 1,
		fontSize: 12.5,
		color: theme.palette.text.primary
	},
	searchClear: {
		width: 18,
		height: 18,
		borderRadius: '50%',
		color: theme.palette.text.secondary,
		animation: '$fadeIn 160ms ease both',
		transition: theme.transitions.create(['color', 'transform'], {duration: 150}),
		'&:hover': {
			color: theme.palette.text.primary,
			transform: 'rotate(90deg)'
		}
	},
	searchClearIcon: {
		fontSize: 13
	},
	list: {
		marginTop: 6,
		padding: '0 6px',
		overflowY: 'auto',
		...scrollbarStyles(theme)
	},
	listBox: {
		maxHeight: ({listHeight}) => listHeight
	},
	row: {
		width: '100%',
		height: ROW_HEIGHT,
		padding: '0 8px',
		borderRadius: 7,
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'flex-start',
		textAlign: 'left',
		transition: theme.transitions.create(['background-color', 'transform'], {duration: 150}),
		'&:hover': {
			backgroundColor: fade(theme.palette.primary.main, 0.12),
			transform: 'translateX(2px)'
		}
	},
	rowSelected: {
		backgroundColor: fade(theme.palette.primary.main, 0.08)
	},
	box: {
		width: 16,
		height: 16,
		flexShrink: 0,
		marginRight: 10,
		borderRadius: 4,
		border: `1.5px solid ${fade(theme.palette.text.primary, 0.32)}`,
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		transition: theme.transitions.create(['background-color', 'border-color'], {duration: 160})
	},
	boxChecked: {
		borderColor: theme.palette.primary.main,
		backgroundColor: theme.palette.primary.main
	},
	tick: {
		fontSize: 13,
		color: theme.palette.getContrastText(theme.palette.primary.main),
		opacity: 0,
		transform: 'scale(0.4)',
		transition: theme.transitions.create(['opacity', 'transform'], {duration: 160})
	},
	tickChecked: {
		opacity: 1,
		transform: 'scale(1)'
	},
	value: {
		flex: 1,
		minWidth: 0,
		fontSize: 12.5,
		lineHeight: 1.3,
		overflow: 'hidden',
		textOverflow: 'ellipsis',
		whiteSpace: 'nowrap',
		color: theme.palette.text.primary
	},
	valueSelected: {
		fontWeight: 600
	},
	count: {
		marginLeft: 8,
		fontSize: 11,
		fontVariantNumeric: 'tabular-nums',
		color: theme.palette.text.secondary
	},
	divider: {
		height: 1,
		margin: '4px 8px',
		backgroundColor: fade(theme.palette.text.primary, 0.1)
	},
	empty: {
		display: 'flex',
		flexDirection: 'column',
		alignItems: 'center',
		justifyContent: 'center',
		gap: 6,
		height: ROW_HEIGHT * 2,
		color: theme.palette.text.disabled,
		animation: '$fadeIn 220ms ease both'
	},
	emptyIcon: {
		fontSize: 18
	},
	emptyText: {
		fontSize: 11.5
	},
	actions: {
		display: 'flex',
		alignItems: 'center',
		gap: 6,
		padding: '6px 10px 8px 10px'
	},
	action: {
		padding: '3px 9px',
		borderRadius: 6,
		fontSize: 11,
		fontWeight: 600,
		letterSpacing: '0.02em',
		color: theme.palette.text.secondary,
		transition: theme.transitions.create(['background-color', 'color'], {duration: 150}),
		'&:hover': {
			backgroundColor: fade(theme.palette.text.primary, 0.08),
			color: theme.palette.text.primary
		}
	},
	actionDisabled: {
		opacity: 0.38,
		pointerEvents: 'none'
	},
	spacer: {
		flex: 1
	},
	'@media (prefers-reduced-motion: reduce)': {
		card: {animation: 'none'},
		badge: {animation: 'none'},
		row: {'&:hover': {transform: 'none'}}
	}
}));

// MUIDataTable传给custom display回调的column带实时display状态（"true"/"false"/布尔），据此决定是否渲染筛选卡，
// 页面就不必在View Columns切换时重建columns（重建会触发整表重算）
export function isColumnDisplayed(column) {
	const display = column ? column.display : true;
	return display !== false && display !== 'false' && display !== 'excluded';
}

// 单元格值拆成多个筛选值：传separator时按分隔符拆开（如Agent列"(a) a, (b) b"）
export function splitFilterValues(raw, separator) {
	if (raw === null || raw === undefined || typeof raw === 'object') {
		return [];
	}
	const text = String(raw);
	const parts = separator ? text.split(separator) : [text];
	return parts.map(part => part.trim()).filter(part => part !== '');
}

export function buildFilterValueOptions(rows, name, separator) {
	if (!rows || rows.length === 0 || !name) {
		return [];
	}
	const counter = new Map();
	rows.forEach(row => {
		if (!row) {
			return;
		}
		const seen = {};
		splitFilterValues(row[name], separator).forEach(value => {
			if (seen[value]) {
				return;
			}
			seen[value] = true;
			counter.set(value, (counter.get(value) || 0) + 1);
		});
	});
	const options = [];
	counter.forEach((count, value) => options.push({value, count}));
	return options.sort((a, b) => a.value.localeCompare(b.value, undefined, {numeric: true, sensitivity: 'base'}));
}

export function createDataTableFilterTheme(theme) {
	const accent = theme.palette.primary.main;
	const paper = theme.palette.background.paper;
	const isDark = theme.palette.type === 'dark';
	return createTheme({
		...theme,
		overrides: {
			...(theme.overrides || {}),
			MuiPopover: {
				paper: {
					borderRadius: 14,
					border: `1px solid ${fade(theme.palette.text.primary, 0.12)}`,
					boxShadow: isDark
						? '0 20px 52px rgba(0, 0, 0, 0.6)'
						: '0 20px 52px rgba(15, 23, 42, 0.18)'
				}
			},
			MuiGridList: {
				root: {
					margin: '0 !important',
					padding: '14px 16px 18px 16px',
					width: '100%'
				}
			},
			MuiGridListTile: {
				root: {
					padding: '7px !important',
					// 隐藏列的筛选卡返回null → tile > FormControl 为空，整格藏掉避免留空位
					// （弹窗在嵌套ThemeProvider下，MUI class带编号后缀，不能按class名匹配，按结构匹配）
					'&:has(> div > div:empty)': {
						display: 'none'
					}
				}
			},
			MUIDataTableFilter: {
				root: {
					padding: 0,
					width: 720,
					maxWidth: 'calc(100vw - 48px)',
					maxHeight: '72vh',
					overflowY: 'auto',
					backgroundColor: paper,
					fontFamily: 'inherit',
					...scrollbarStyles(theme)
				},
				header: {
					margin: 0,
					padding: '13px 20px 12px 20px',
					alignItems: 'center',
					position: 'sticky',
					top: 0,
					zIndex: 3,
					backgroundColor: paper,
					borderBottom: `1px solid ${fade(theme.palette.text.primary, 0.1)}`
				},
				reset: {
					display: 'flex',
					alignItems: 'center'
				},
				title: {
					marginLeft: 0,
					fontSize: 13,
					fontWeight: 700,
					letterSpacing: '0.12em',
					textTransform: 'uppercase',
					color: theme.palette.text.primary
				},
				resetLink: {
					marginLeft: 18,
					padding: '3px 14px',
					borderRadius: 999,
					fontSize: 11,
					fontWeight: 700,
					letterSpacing: '0.08em',
					color: theme.palette.text.primary,
					border: `1px solid ${fade(accent, 0.45)}`,
					backgroundColor: fade(accent, 0.1),
					transition: theme.transitions.create(['background-color', 'transform'], {duration: 180}),
					'&:hover': {
						backgroundColor: fade(accent, 0.24),
						transform: 'translateY(-1px)'
					},
					'&:active': {
						transform: 'none'
					}
				},
				gridListTile: {
					marginTop: 0
				}
			},
			MUIDataTableFilterList: {
				root: {
					margin: '0 16px',
					padding: '6px 0 0 0'
				},
				chip: {
					margin: '6px 8px 0 0',
					height: 26,
					borderRadius: 8,
					fontSize: 12,
					fontWeight: 600,
					color: theme.palette.text.primary,
					backgroundColor: fade(accent, 0.16),
					border: `1px solid ${fade(accent, 0.4)}`,
					transition: theme.transitions.create(['background-color', 'transform', 'box-shadow'], {duration: 170}),
					'&:hover': {
						backgroundColor: fade(accent, 0.28),
						transform: 'translateY(-1px)',
						boxShadow: isDark
							? '0 6px 14px rgba(0, 0, 0, 0.4)'
							: '0 6px 14px rgba(15, 23, 42, 0.12)'
					},
					'& .MuiChip-deleteIcon': {
						color: fade(theme.palette.text.primary, 0.45),
						transition: theme.transitions.create(['color', 'transform'], {duration: 150}),
						'&:hover': {
							color: theme.palette.error.main,
							transform: 'scale(1.15) rotate(90deg)'
						}
					}
				}
			}
		}
	});
}

function FilterOptionRow({option, checked, classes, onToggle, style}) {
	const text = (
		<span className={`${classes.value} ${checked ? classes.valueSelected : ''}`}>{option.value}</span>
	);
	return (
		<ButtonBase
			focusRipple
			style={style}
			aria-pressed={checked}
			className={`${classes.row} ${checked ? classes.rowSelected : ''}`}
			onClick={() => onToggle(option.value)}>
			<span className={`${classes.box} ${checked ? classes.boxChecked : ''}`}>
				<CheckRoundedIcon className={`${classes.tick} ${checked ? classes.tickChecked : ''}`}/>
			</span>
			{option.value.length > TOOLTIP_FROM ? (
				<Tooltip title={option.value} enterDelay={600}>{text}</Tooltip>
			) : text}
			{option.count > 0 && (<span className={classes.count}>{option.count}</span>)}
		</ButtonBase>
	);
}

export default function DataTableFilter({index, label, name, rows, selected, onChange, language, separator}) {
	const [keyword, setKeyword] = useState('');
	const [focused, setFocused] = useState(false);
	const selectedList = selected || [];
	const selectedRef = useRef(selectedList);
	selectedRef.current = selectedList;

	const options = useMemo(() => {
		const built = buildFilterValueOptions(rows, name, separator);
		const known = {};
		built.forEach(option => {
			known[option.value] = true;
		});
		const orphans = selectedRef.current
			.filter(value => !known[value])
			.map(value => ({value, count: 0}));
		return orphans.concat(built);
	}, [rows, name, separator]);

	// 已选值置顶，快照只在关键字或选项变化时更新，避免勾选瞬间列表跳动
	const pinnedRef = useRef({});
	const pinnedKeyRef = useRef(null);
	const pinnedKey = `${keyword}|${options.length}`;
	if (pinnedKeyRef.current !== pinnedKey) {
		pinnedKeyRef.current = pinnedKey;
		const map = {};
		selectedRef.current.forEach(value => {
			map[value] = true;
		});
		pinnedRef.current = map;
	}
	const pinned = pinnedRef.current;

	const visibleOptions = useMemo(() => {
		const key = keyword.trim().toLowerCase();
		const matched = key
			? options.filter(option => option.value.toLowerCase().indexOf(key) >= 0)
			: options;
		const top = matched.filter(option => pinned[option.value]);
		const rest = matched.filter(option => !pinned[option.value]);
		return top.concat(rest);
	}, [options, keyword, pinned]);

	const selectedMap = useMemo(() => {
		const map = {};
		selectedList.forEach(value => {
			map[value] = true;
		});
		return map;
	}, [selectedList]);

	const onToggle = (value) => {
		if (selectedMap[value]) {
			onChange(selectedList.filter(item => item !== value));
		} else {
			onChange(selectedList.concat(value));
		}
	};

	const onSelectAll = () => {
		const next = selectedList.slice();
		visibleOptions.forEach(option => {
			if (!selectedMap[option.value]) {
				next.push(option.value);
			}
		});
		onChange(next);
	};

	const onClear = () => {
		setKeyword('');
		onChange([]);
	};

	const listHeight = Math.max(ROW_HEIGHT, Math.min(visibleOptions.length, MAX_VISIBLE_ROWS) * ROW_HEIGHT);
	const virtualized = visibleOptions.length > VIRTUALIZE_FROM;
	const classes = useStyles({index, listHeight});

	return (
		<div className={`${classes.card} ${selectedList.length > 0 ? classes.cardActive : ''}`}>
			<div className={classes.head}>
				<Typography component="span" className={classes.label}>{label}</Typography>
				{selectedList.length > 0 && (
					<span key={selectedList.length} className={classes.badge}>{selectedList.length}</span>
				)}
			</div>
			<div className={`${classes.search} ${focused ? classes.searchActive : ''}`}>
				<SearchRoundedIcon className={classes.searchIcon}/>
				<InputBase
					value={keyword}
					placeholder={language.search}
					className={classes.searchInput}
					inputProps={{'aria-label': label}}
					onFocus={() => setFocused(true)}
					onBlur={() => setFocused(false)}
					onChange={(e) => setKeyword(e.target.value)}
				/>
				{keyword !== '' && (
					<ButtonBase className={classes.searchClear} onClick={() => setKeyword('')}>
						<CloseRoundedIcon className={classes.searchClearIcon}/>
					</ButtonBase>
				)}
			</div>
			{visibleOptions.length === 0 ? (
				<div className={classes.empty}>
					<FilterNoneRoundedIcon className={classes.emptyIcon}/>
					<Typography component="span" className={classes.emptyText}>{language.no_data}</Typography>
				</div>
			) : virtualized ? (
				<FixedSizeList
					height={listHeight}
					width="100%"
					itemSize={ROW_HEIGHT}
					itemCount={visibleOptions.length}
					className={classes.list}>
					{({index: rowIndex, style}) => (
						<FilterOptionRow
							style={style}
							classes={classes}
							option={visibleOptions[rowIndex]}
							checked={!!selectedMap[visibleOptions[rowIndex].value]}
							onToggle={onToggle}
						/>
					)}
				</FixedSizeList>
			) : (
				<div className={`${classes.list} ${classes.listBox}`}>
					{visibleOptions.map(option => (
						<FilterOptionRow
							key={option.value}
							classes={classes}
							option={option}
							checked={!!selectedMap[option.value]}
							onToggle={onToggle}
						/>
					))}
				</div>
			)}
			<div className={classes.divider}/>
			<div className={classes.actions}>
				<ButtonBase
					className={`${classes.action} ${visibleOptions.length === 0 ? classes.actionDisabled : ''}`}
					onClick={onSelectAll}>
					{language.select_all}
				</ButtonBase>
				<div className={classes.spacer}/>
				<ButtonBase
					className={`${classes.action} ${selectedList.length === 0 ? classes.actionDisabled : ''}`}
					onClick={onClear}>
					{language.clear}
				</ButtonBase>
			</div>
		</div>
	);
}
